import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, MessageSquare, Bot, Zap, CalendarDays, Clock, FileText, FilePlus2, FileEdit, HardDrive } from 'lucide-react';
import apiService from '../services/apiService';
import DocumentPreviewModal from './DocumentPreviewModal';
import { useLanguage } from '../i18n';
import { formatBytes } from '../utils/formatBytes';

// Conversation Data card (Conversation Data ticket). Detailed per-conversation
// metrics, pinned to the LEFT of the workspace (immediately right of the
// sidebar) at a middle vertical level — NOT a centered modal — with an X to
// dismiss. Also surfaces the conversation's "documentary footprint" — the docs
// CREATED or EDITED within it, as clickable links (Conversation documents #148).
const fmtDate = (iso) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    } catch (_) { return '—'; }
};
const fmtDateTime = (iso) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString(undefined, {
            year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
        });
    } catch (_) { return '—'; }
};

const Row = ({ Icon, label, value, sublabel, testId }) => (
    <div
        data-testid={testId}
        className="flex items-center justify-between gap-4 py-2 border-b border-pai-hairline last:border-0"
    >
        <span className="flex items-start gap-2 text-sm text-pai-meta min-w-0">
            <Icon className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span className="min-w-0">
                <span className="block">{label}</span>
                {sublabel && (
                    <span className="block text-[11px] text-pai-faint">{sublabel}</span>
                )}
            </span>
        </span>
        <span className="text-sm font-semibold text-white text-right flex-shrink-0">{value}</span>
    </div>
);

// One clickable document link that opens the file immediately (#148).
const DocLink = ({ Icon, doc, onOpen, t }) => (
    <button
        type="button"
        data-testid="conv-doc-item"
        onClick={() => onOpen(doc)}
        title={t('msgConvDataOpenDoc', 'Open {name}', { name: doc.name })}
        className="w-full flex items-center gap-2 py-1.5 px-1 text-left text-sm text-blue-600 dark:text-blue-400 hover:underline hover:bg-white/[0.07] rounded"
    >
        <Icon className="w-4 h-4 flex-shrink-0 text-pai-faint" />
        <span className="truncate">{doc.name}</span>
        {doc.version > 1 && (
            <span className="flex-shrink-0 ml-auto text-[10px] font-semibold text-pai-faint">v{doc.version}</span>
        )}
    </button>
);

const DocumentsSection = ({ docs, error, onOpen, t }) => {
    const created = docs?.created || [];
    const edited = docs?.edited || [];
    const isEmpty = docs && created.length === 0 && edited.length === 0;

    return (
        <div data-testid="conversation-documents-section" className="mt-3 pt-2 border-t border-pai-hairline">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-pai-faint mb-1">
                <FileText className="w-3.5 h-3.5" />
                {t('msgConvDataDocumentsTitle', 'Documents in this Conversation')}
            </div>
            {error ? (
                <div className="py-3 text-center text-xs text-pai-faint">{t('msgConvDataDocsLoadError', "Couldn't load documents.")}</div>
            ) : !docs ? (
                <div className="py-3 text-center text-xs text-pai-faint">{t('msgConvDataDocsLoading', 'Loading…')}</div>
            ) : isEmpty ? (
                <div className="py-3 text-center text-xs text-pai-faint">{t('msgConvDataDocsEmpty', 'No documents created or edited here.')}</div>
            ) : (
                <div className="max-h-44 overflow-y-auto -mx-1">
                    {created.length > 0 && (
                        <div className="mb-1" data-testid="conv-docs-created">
                            <div className="px-1 text-[11px] font-medium text-pai-meta">{t('msgConvDataDocsCreated', 'Created')}</div>
                            {created.map((d) => (
                                <DocLink key={`c-${d.id}`} Icon={FilePlus2} doc={d} onOpen={onOpen} t={t} />
                            ))}
                        </div>
                    )}
                    {edited.length > 0 && (
                        <div data-testid="conv-docs-edited">
                            <div className="px-1 text-[11px] font-medium text-pai-meta">{t('msgConvDataDocsEdited', 'Edited')}</div>
                            {edited.map((d) => (
                                <DocLink key={`e-${d.id}`} Icon={FileEdit} doc={d} onOpen={onOpen} t={t} />
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const ConversationDataCard = ({ conversationId, title, onClose }) => {
    const { t } = useLanguage();
    const [data, setData] = useState(null);
    const [error, setError] = useState(false);
    const [docs, setDocs] = useState(null);
    const [docsError, setDocsError] = useState(false);
    const [preview, setPreview] = useState(null); // { id, name } of the doc being previewed
    const cardRef = useRef(null);

    useEffect(() => {
        let alive = true;
        setData(null);
        setError(false);
        setDocs(null);
        setDocsError(false);
        setPreview(null);
        apiService.getConversationData(conversationId)
            .then((d) => { if (alive) setData(d); })
            .catch(() => { if (alive) setError(true); });
        apiService.getConversationDocumentHistory(conversationId)
            .then((d) => { if (alive) setDocs(d); })
            .catch(() => { if (alive) setDocsError(true); });
        return () => { alive = false; };
    }, [conversationId]);

    // Esc closes the previewed document first (if open), otherwise the card.
    useEffect(() => {
        const onKey = (e) => {
            if (e.key !== 'Escape') return;
            if (preview) { setPreview(null); return; }
            onClose();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose, preview]);

    // Click-away dismissal (Conversation data #434). The card used to close ONLY
    // via its X or Escape, so it stayed painted over whatever the user did next —
    // a kebab Delete opened its confirmation UNDER the card. Any pointer press
    // outside the card now closes it, in the CAPTURE phase so it runs before the
    // pressed control's own handler (that action then proceeds on a clean
    // screen). Suspended while a document preview is open: that modal is portaled
    // to <body>, outside the card's DOM, and pressing inside it must not dismiss
    // the card underneath.
    useEffect(() => {
        if (preview) return undefined;
        const onPointerDown = (e) => {
            if (cardRef.current && cardRef.current.contains(e.target)) return;
            onClose();
        };
        document.addEventListener('pointerdown', onPointerDown, true);
        return () => document.removeEventListener('pointerdown', onPointerDown, true);
    }, [onClose, preview]);

    // PORTALED to <body>. It is rendered inside the conversation pane, and
    // `.pai-panel` sets `isolation: isolate` — which scopes every z-index inside a
    // panel to that panel, so this card could not paint above the chat pane beside
    // it and its close button was unclickable. The left offset also has to clear
    // the design's wider columns: 14 frame + 250 sidebar + 14 gap + 300 list + 14.
    return createPortal(
        <div
            data-testid="conversation-data-card"
            ref={cardRef}
            // #432: in the mobile shell there is no 592px of columns to clear — the
            // card is centred and capped to the screen instead. `shell:` restores
            // the desktop placement exactly.
            className="fixed left-1/2 -translate-x-1/2 w-[min(20rem,calc(100vw-2rem))] max-h-[85dvh] overflow-y-auto shell:left-[592px] shell:translate-x-0 shell:w-80 shell:max-w-[calc(100vw-612px)] shell:max-h-none shell:overflow-visible top-1/2 -translate-y-1/2 z-[75] bg-pai-modal border border-pai rounded-modal shadow-modal"
            role="dialog"
            aria-label={t('msgConvDataDialogAria', 'Conversation data')}
        >
            <div className="flex items-start justify-between gap-2 px-4 pt-3 pb-2 border-b border-pai-hairline">
                <div className="min-w-0">
                    <div className="text-xs font-semibold uppercase tracking-wide text-pai-faint">{t('msgConvDataHeader', 'Conversation Data')}</div>
                    <div className="text-sm font-medium text-white truncate">{title || t('msgConvDataDefaultTitle', 'Conversation')}</div>
                </div>
                <button
                    onClick={onClose}
                    aria-label={t('msgConvDataCloseAria', 'Close conversation data')}
                    className="flex-shrink-0 p-1 rounded text-pai-faint hover:text-white  hover:bg-white/[0.07]"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>
            <div className="px-4 py-2">
                {error ? (
                    <div className="py-6 text-center text-sm text-pai-meta">{t('msgConvDataLoadError', "Couldn't load conversation data.")}</div>
                ) : !data ? (
                    <div className="py-6 text-center text-sm text-pai-faint">{t('msgConvDataLoading', 'Loading…')}</div>
                ) : (
                    <>
                        <Row Icon={MessageSquare} label={t('convDataUserMessages', 'User Messages')} value={data.user_messages ?? 0} />
                        <Row Icon={Bot} label={t('convDataAiAnswers', 'AI Answers')} value={data.ai_answers ?? 0} />
                        {/* What this thread COST to produce — spent once, in the past.
                            #354: this is now the REAL, PRICED Energy Points figure
                            (`energy_pts`, a 2dp string rendered verbatim — never
                            re-rounded), not the raw LLM token count `energy_points`
                            used to be mislabeled as. That mislabeling is exactly how
                            a single message came to read as 37,893 "points" against a
                            several-thousand-point monthly tank — a number pricing now
                            makes impossible by construction, since it goes through the
                            SAME per-row rate the Energy Receipt (#349) uses. An em-dash
                            means "we couldn't price it" (never a fabricated 0.00). */}
                        <Row
                            Icon={Zap}
                            testId="conv-data-interaction-energy"
                            label={t('convDataInteractionEnergy', 'Total Interaction Energy')}
                            sublabel={t('convDataInteractionEnergyHint', 'Spent once, to produce this thread')}
                            value={data.energy_pts == null ? '—' : `${data.energy_pts} ${t('convDataPtsUnit', 'pts')}`}
                        />
                        {/* What it costs to KEEP — charged every cycle, for as long as it
                            exists. A different question from the row above (a one-time
                            past cost vs. a recurring reservation), so it gets its own
                            line and its own hint rather than being folded into one
                            "energy" number — even though both are now the SAME unit. */}
                        <Row
                            Icon={HardDrive}
                            testId="conv-data-storage-cost"
                            label={t('convDataStorageCost', 'Memory Storage Cost')}
                            sublabel={t('convDataStorageCostHint', 'Reserved per month (Gray Stack)')}
                            /* The BE's 2dp string, rendered VERBATIM — never re-rounded. */
                            value={
                                data.storage_pts_per_month == null
                                    ? '—'
                                    : `${data.storage_pts_per_month} pts${
                                        formatBytes(data.storage_bytes) ? ` · ${formatBytes(data.storage_bytes)}` : ''
                                    }`
                            }
                        />
                        <Row Icon={CalendarDays} label={t('convDataStartingDate', 'Starting Date')} value={fmtDate(data.created_at)} />
                        <Row Icon={Clock} label={t('convDataLastUsed', 'Last Used')} value={fmtDateTime(data.last_used)} />
                    </>
                )}

                {/* "Documents in this Conversation" — created + edited footprint (#148). */}
                <DocumentsSection docs={docs} error={docsError} onOpen={setPreview} t={t} />
            </div>

            {/* Clicking a document name opens it immediately in the standard preview. */}
            {preview && (
                <DocumentPreviewModal
                    documentId={preview.id}
                    documentName={preview.name}
                    onClose={() => setPreview(null)}
                />
            )}
        </div>,
        document.body,
    );
};

export default ConversationDataCard;
